<script setup lang="ts">
/**
 * Body of /how-to-cancel/<slug>: how to leave one competitor's subscription,
 * step by step, in its own words and with a source on every block.
 *
 * Same rules as AlternativeGuide.vue (EU comparative advertising): facts as
 * the competitor states them, no opinion about their product. The page helps
 * first; our offer comes once, at the end, for readers leaving because of the
 * renewals. English only, so the copy is written here rather than translated.
 */
import { formatUsd, PRICES, JOB_PACK_DAYS } from '~/constants/pricing'
import type { Competitor } from '~/constants/competitors'
import type { CancelGuide } from '~/constants/cancelGuides'

const props = defineProps<{ guide: CancelGuide, competitor: Competitor }>()

const { t } = useI18n()

const checkedLabel = computed(() =>
  new Date(`${props.guide.checked}T12:00:00Z`).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
)

const exportPrice = formatUsd(PRICES.exportCredit)
const packPrice = formatUsd(PRICES.jobPack)
</script>

<template>
  <div class="sofax">
    <!-- The answer -->
    <section class="bg-white dark:bg-gray-950">
      <div class="section-space-sm">
        <div class="shell">
          <div class="mx-auto max-w-3xl rounded-[0.625rem] border-l-4 border-accent bg-surface p-[1.875rem] dark:bg-gray-800">
            <div class="mb-3 text-sm font-semibold uppercase tracking-wide text-muted dark:text-gray-400">
              The short answer
            </div>
            <p class="text-lg">{{ guide.short }}</p>
            <p class="mt-4 text-sm text-muted dark:text-gray-400">
              Taken from {{ competitor.name }}'s own pages on {{ checkedLabel }}, each linked below. Its menus and terms can change, so check the source if a step looks different.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- The steps, one block per way of cancelling -->
    <section class="bg-white dark:bg-gray-950">
      <div class="section-space-bottom">
        <div class="shell">
          <SofaxSectionHeading
            eyebrow="Step by step"
            :title="`How to cancel your ${competitor.name} subscription`"
            width="max-w-4xl"
          />
          <div class="mx-auto flex max-w-4xl flex-col gap-6">
            <div
              v-for="method in guide.methods"
              :key="method.title"
              class="rounded-[0.625rem] bg-surface p-[1.875rem] dark:bg-gray-800"
            >
              <h3 class="!text-xl md:!text-2xl">{{ method.title }}</h3>
              <ol class="mt-5 flex flex-col gap-4">
                <li v-for="(step, index) in method.steps" :key="index" class="flex items-start gap-4">
                  <span
                    class="inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-ink !text-sm !font-bold !text-white dark:bg-white dark:!text-ink"
                    aria-hidden="true"
                  >{{ index + 1 }}</span>
                  <span class="pt-1 text-base">{{ step }}</span>
                </li>
              </ol>
              <p class="mt-5">
                <a :href="method.source" target="_blank" rel="noopener nofollow" class="link text-sm">{{ t('alternatives.page.source') }}</a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Timing, refund, what you keep -->
    <section class="surface-alt">
      <div class="section-space">
        <div class="shell">
          <SofaxSectionHeading
            eyebrow="Before you cancel"
            title="When to cancel, refunds, and what happens to your resume"
            width="max-w-4xl"
          />
          <div class="mx-auto grid max-w-4xl grid-cols-1 gap-4 md:grid-cols-2">
            <div class="rounded-[0.625rem] bg-white p-[1.875rem] dark:bg-gray-800">
              <h3 class="!text-xl">When to cancel</h3>
              <p class="mt-3 text-base">
                {{ guide.timing.text }}
                <a :href="guide.timing.source" target="_blank" rel="noopener nofollow" class="link text-sm">{{ t('alternatives.page.source') }}</a>
              </p>
            </div>
            <div v-if="guide.refund" class="rounded-[0.625rem] bg-white p-[1.875rem] dark:bg-gray-800">
              <h3 class="!text-xl">Refunds</h3>
              <p class="mt-3 text-base">
                {{ guide.refund.text }}
                <a :href="guide.refund.source" target="_blank" rel="noopener nofollow" class="link text-sm">{{ t('alternatives.page.source') }}</a>
              </p>
            </div>
            <div class="rounded-[0.625rem] bg-white p-[1.875rem] md:col-span-2 dark:bg-gray-800">
              <h3 class="!text-xl">After you cancel</h3>
              <p class="mt-3 text-base">
                {{ guide.after.text }}
                <a :href="guide.after.source" target="_blank" rel="noopener nofollow" class="link text-sm">{{ t('alternatives.page.source') }}</a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- The one offer, for readers leaving because of the renewals -->
    <section class="bg-white dark:bg-gray-950">
      <div class="section-space">
        <div class="shell">
          <div class="mx-auto max-w-3xl text-center">
            <div class="eyebrow mb-5">Still need the resume?</div>
            <h2 class="!text-3xl md:!text-[2.5rem]">Keep your resume without another subscription</h2>
            <p class="mt-5 text-lg">
              Paste your current resume into InMyResume, even the plain text export, and the AI lays it out on an ATS-friendly template. The Basic template downloads free as a PDF, with no watermark and no account. The other templates cost {{ exportPrice }} for one clean PDF, or {{ packPrice }} for {{ JOB_PACK_DAYS }} days of everything, paid once. Nothing renews.
            </p>
            <div class="mt-8">
              <SofaxBtn to="/free-cv-builder" label="Paste your resume, no account needed" variant="accent" />
            </div>
            <p class="mt-6">
              <NuxtLink :to="`/alternatives/${competitor.slug}`" class="link">{{ competitor.name }} pricing and plans compared with ours →</NuxtLink>
            </p>
            <p class="mt-10 text-sm text-muted dark:text-gray-400">{{ t('alternatives.page.trademark', { name: competitor.name }) }}</p>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>
